import api from '../../../shared/utils/api';
import type { Contact, PaginatedResponse } from '../../../shared/types';
import { useEnrichmentQueueStore } from '../../../shared/stores/useEnrichmentQueueStore';

export interface CreateContactPayload {
  dealId: string;
  nome: string;
  cargo?: string;
  /** Janus seniority taxonomy key (e.g. 'manager', 'c_suite'). */
  seniority?: string;
  /** Janus department taxonomy key (e.g. 'sales', 'technology'). */
  department?: string;
  email?: string;
  telefone?: string;
  /** All emails (primary = first). Backend stores extras as data points. */
  emails?: string[];
  /** All phones (primary = first). */
  phones?: string[];
  linkedinUrl?: string;
  eDecisor?: boolean;
  observacoes?: string;
}

export interface UpdateContactPayload {
  nome?: string;
  cargo?: string;
  seniority?: string;
  department?: string;
  email?: string;
  telefone?: string;
  emails?: string[];
  phones?: string[];
  linkedinUrl?: string;
  eDecisor?: boolean;
  observacoes?: string;
}

export interface ConfirmLinkedInPayload {
  dealId: string;
  linkedinUrl: string;
  fullName: string;
  title?: string;
  companyName?: string;
  companyCnpj?: string;
  email?: string;
  telefone?: string;
}

export interface ContactDiscoveryOptions {
  discoveryStrategy?: 'leadership_v1';
  discoveryTargetCount?: number;
  providerConsent?: { apollo: boolean };
  discoveryBudget?: { maxProviderCalls: number; maxLushaCredits: number };
}

export interface ContactSearchPayload extends ContactDiscoveryOptions {
  dealId?: string;
  // Somente ação manual; abertura automática continua cache-first.
  searchIntent?: 'deep';
  companyName: string;
  companyDomain?: string;
  companyCnpj?: string;
}

export interface ContactPreview {
  contactId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  /** pt-BR-normalized job title (display); falls back to jobTitle. */
  titlePt?: string;
  companyName: string;
  linkedinUrl?: string;
  isLeadership?: boolean;
  discoverySources?: string[];
  discoveryQuality?: { previewOnly?: boolean; placeholder?: boolean; emailDomainMismatch?: boolean; reasons?: string[] };
}

// Metadados aditivos retornados pelo BFF; cursores e seleção ficam no servidor.
export interface DiscoveryPagination {
  provider?: string;
  page?: number;
  pageSize?: number;
  nextPage?: number | null;
  total?: number | null;
  exhausted?: boolean;
  capReached?: boolean;
  strategy?: string;
  providerCursors?: Record<string, DiscoveryPagination>;
  phases?: Record<string, DiscoveryPagination>;
}

export interface DiscoveryProvider {
  provider: string;
  status: 'consulted' | 'skipped' | string;
  reason?: string | null;
  returnedCount?: number;
  newCount?: number;
  calls?: number;
  creditsCharged?: number;
  costStatus?: string;
  filters?: Record<string, unknown>;
}

export interface DiscoveryReport {
  enabled?: boolean;
  ready?: boolean;
  strategy?: string;
  targetCount?: number;
  targetReached?: boolean;
  filterSnapshot?: Record<string, unknown>;
  providers?: DiscoveryProvider[];
  nextProvider?: {
    provider: string;
    requiresConsent?: boolean;
    available: boolean;
    reason?: string | null;
    maxProviderCalls?: number;
    maxLushaCredits?: number;
    costStatus?: string;
  } | null;
}

export interface DiscoveryMetadata {
  reused?: boolean;
  discovery?: DiscoveryReport | null;
  discoveryReceipt?: DiscoveryMetadata | null;
  discoveryQualitySummary?: {
    placeholderCount?: number;
    quarantinedCount?: number;
    reasons?: Record<string, number>;
  };
  status?: string;
  messageId?: string;
  inFlight?: boolean;
  reconciliationPending?: boolean;
  provider?: string;
  pagination?: DiscoveryPagination | null;
  providerSelection?: Record<string, unknown> | null;
  exhausted?: boolean;
}

export type ContactListResponse = PaginatedResponse<Contact> & DiscoveryMetadata;

export interface SearchMoreContactsResponse extends DiscoveryMetadata {
  hasMore: boolean;
  exhausted: boolean;
  nextPage: number;
  total?: number | null;
  pageResults?: number;
  newContacts?: number;
  creditsDeducted?: number;
  inFlight?: boolean;
}

export interface ContactSearchResponse extends DiscoveryMetadata {
  found: boolean;
  contacts: ContactPreview[];
  totalResults: number;
  requestId?: string;
  error?: string;
  // Async dispatch path. In production the sales-api runs with
  // ENRICHMENT_USE_PUBSUB=true, so /contacts/search does not run providers inline —
  // it publishes the request to the enrichment worker and returns a processing
  // handle. The caller must poll /contacts/enrichment-status/{messageId} until
  // the terminal state carries the actual ContactSearchResponse in `result`.
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'not_found';
  messageId?: string;
}

export interface ContactUnlockPayload {
  contactId: string;
  requestId: string;
  dealId: string;
  fullName: string;
  jobTitle?: string;
  companyName?: string;
  companyCnpj?: string;
}

export interface ContactUnlockResponse {
  success: boolean;
  contactId: string;
  fullName: string;
  jobTitle?: string;
  email?: string;
  telefone?: string;
  linkedinUrl?: string;
  source: string;
  creditsDeducted: number;
  creditsRemaining?: number;
  error?: string;
  /** Frase pt-BR pronta para exibir quando `success` é false (janus-sales-api, aditivo). */
  message?: string;
  enrichmentMessageId?: string;
}

export interface EnrichmentStatusResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'no_data' | 'not_found';
  result:
    | ContactSearchResponse
    | ContactUnlockResponse
    | LinkedInPostsResult
    | ProfileConsolidationResult
    | null;
}

/** One recent LinkedIn post for a contact (locked contract — snake_case). */
export interface LinkedInContactPost {
  text: string;
  url: string;
  posted_at: string;
  reactions?: number;
}

/** Result payload of the linkedin-posts async enrichment (≤5 posts). */
export interface LinkedInPostsResult {
  posts: LinkedInContactPost[];
}

/** One professional experience entry (locked contract — start/end strings). */
export interface ContactExperience {
  company: string;
  title: string;
  start: string;
  end: string | null;
  description?: string | null;
}

/** Profile header brought back by the linkedin-experience enrichment. */
export interface ConsolidatedProfile {
  full_name: string;
  headline: string;
  location: string;
  linkedin_url: string;
}

/**
 * Decision-power sub-score of the deep experience analysis. `score` is a
 * 0–100 indicator of how much purchasing/decision authority the contact
 * likely has; `rationale` is a short pt-BR justification.
 */
export interface DecisionPower {
  score: number;
  rationale: string;
}

/**
 * Structured, AI-generated deep analysis of a contact's career
 * (locked contract — snake_case). Carried both by the live
 * `linkedin-experience` poll result and the persisted read-back. Every
 * field is optional so the renderer degrades gracefully to the plain
 * `summary` string when the deep shape is absent.
 */
export interface ExperienceAnalysis {
  /** Narrative summary of the contact's career (rich prose). */
  summary?: string;
  /** One-line description of the career arc / trajectory. */
  career_trajectory?: string;
  /** Free-string seniority label, e.g. "Diretoria", "C-level", "Gerência". */
  seniority_level?: string;
  /** Estimated decision/purchasing power, scored + justified. */
  decision_power?: DecisionPower;
  /** What the contact most likely cares about right now. */
  likely_priorities?: string[];
  /** Actionable conversation hooks a seller can open with. */
  conversation_angles?: string[];
}

/** Result payload of the linkedin-experience async enrichment. */
export interface ProfileConsolidationResult {
  profile: ConsolidatedProfile;
  experiences: ContactExperience[];
  /** Consolidated AI narrative of the contact's career (plain text). */
  summary: string;
  /**
   * Deep, structured analysis (RP-04). Present when the worker ran the
   * richer analysis prompt; absent on older jobs (fall back to `summary`).
   */
  analysis?: ExperienceAnalysis | null;
}

/**
 * Persisted LinkedIn enrichment data for a contact, read on drawer open
 * (`GET /contacts/{id}/linkedin-data`). Lets the drawer render saved
 * posts/experience instantly without re-running (and re-charging) the
 * enrichment. The BFF camelCases the snake_case backend payload.
 *
 * Tri-state per section is derived from these flags:
 *  - `*Unlocked` true  → this tenant has paid for / owns the data → render it.
 *  - `*Unlocked` false but `poolHas*` true → another tenant filled the pool;
 *    show a "Disponível — N créditos para visualizar" CTA (same enqueue,
 *    served from the pool for the charge).
 *  - neither       → nothing yet; show the normal "Buscar (10 créditos)" CTA.
 */
export interface LinkedInDataReadback {
  /** Persisted posts (empty array when none saved). */
  posts: LinkedInContactPost[];
  /** ISO timestamp the posts were last fetched, or null. */
  postsFetchedAt: string | null;
  /** Monotonic version of the persisted posts (1-based), or null. */
  postsVersion: number | null;
  /** This tenant has the posts unlocked (paid / owned). */
  postsUnlocked: boolean;
  /** Persisted experience timeline (empty array when none saved). */
  experience: ContactExperience[];
  /** Plain-text AI summary (fallback when no structured analysis). */
  experienceSummary: string | null;
  /** Structured deep analysis, when available. */
  experienceAnalysis: ExperienceAnalysis | null;
  /** ISO timestamp the experience was last fetched, or null. */
  experienceFetchedAt: string | null;
  /** Monotonic version of the persisted experience (1-based), or null. */
  experienceVersion: number | null;
  /** This tenant has the experience unlocked (paid / owned). */
  experienceUnlocked: boolean;
  /** The shared contact_core pool already has posts (filled by any tenant). */
  poolHasPosts: boolean;
  /** The shared contact_core pool already has experience. */
  poolHasExperience: boolean;
}

export interface CheckUnlocksResponse {
  unlocked: Record<string, { unlocked_at: string | null }>;
}

export interface ContactReportPayload {
  contactId: string;
  suggestedNome?: string;
  suggestedCargo?: string;
  suggestedEmail?: string;
  suggestedTelefone?: string;
  suggestedLinkedinUrl?: string;
  observacao?: string;
}

export interface ContactReportResponse {
  id: string;
  contactId: string;
  status: string;
  createdAt: string;
}

export const contactsApi = {
  listByDeal: async (dealId: string): Promise<ContactListResponse> => {
    const { data } = await api.get<ContactListResponse>(
      `/contacts/by-deal/${dealId}`,
    );
    return data;
  },

  /**
   * List contacts for a company from the shared contact_core pool, keyed by
   * CNPJ. This is the cross-tenant read path: every tenant with a deal for
   * the same CNPJ sees the same decisor list. Backs the Deal "Contatos" tab.
   */
  listByCompany: async (cnpj: string): Promise<ContactListResponse> => {
    const { data } = await api.get<ContactListResponse>(
      `/contacts/by-company/${encodeURIComponent(cnpj)}`,
    );
    return data;
  },

  /** B7 (C2): copia um contato da Conta Comercial para o deal — sem custo de reveal. */
  createFromAccountContact: async (payload: {
    dealId: string;
    accountContactId: string;
    eDecisor?: boolean;
  }): Promise<Contact> => {
    const { data } = await api.post<Contact>('/contacts/from-account-contact', payload);
    return data;
  },

  create: async (payload: CreateContactPayload): Promise<Contact> => {
    const { data } = await api.post<Contact>('/contacts', payload);
    return data;
  },

  /**
   * On-demand materialisation: turn a company-scoped contact (from the shared
   * `contact_core` pool, listed via {@link listByCompany}) into a deal-scoped
   * legacy `contacts` row, creating it only if needed. Idempotent.
   *
   * Used by the cadence flow: cadences FK into the legacy `contacts` table, so
   * before creating a cadence for a discovered company contact we ensure its
   * deal row exists and use the returned id. Avoids bulk-materialising every
   * discovered contact just because a deal was opened.
   */
  ensureForDeal: async (dealId: string, contactCoreId: string): Promise<Contact> => {
    const { data } = await api.post<Contact>('/contacts/ensure-for-deal', {
      dealId,
      contactCoreId,
    // Caller (ContactDetailDrawer) shows a friendly message.error on failure;
    // suppress the global red toast so we don't double up.
    }, { __skipErrorNotification: true } as never);
    return data;
  },

  update: async (id: string, payload: UpdateContactPayload): Promise<Contact> => {
    const { data } = await api.patch<Contact>(`/contacts/${id}`, payload);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/contacts/${id}`);
  },

  confirmLinkedIn: async (payload: ConfirmLinkedInPayload): Promise<Contact> => {
    const { data } = await api.post<Contact>('/contacts/confirm-linkedin', payload);
    return data;
  },

  searchContacts: async (payload: ContactSearchPayload): Promise<ContactSearchResponse> => {
    const { data } = await api.post<ContactSearchResponse>('/contacts/search', payload);
    return data;
  },

  // Próxima página da fonte selecionada pelo servidor; custo e cursor não são fixados na UI.
  searchMoreContacts: async (payload: ContactDiscoveryOptions & {
    companyCnpj: string;
    dealId?: string;
  }): Promise<SearchMoreContactsResponse> => {
    const { data } = await api.post('/contacts/search-more', payload);
    return data;
  },

  unlockContact: async (payload: ContactUnlockPayload): Promise<ContactUnlockResponse> => {
    const { data } = await api.post<ContactUnlockResponse>('/contacts/unlock', payload);
    // Side effect: feed the global enrichment queue store so the Header
    // icon + inline AI Enriching badge render immediately, regardless of
    // which caller invoked the unlock. The store dedupes by messageId.
    if (data.success && data.enrichmentMessageId) {
      const label = [payload.fullName, payload.companyName]
        .filter(Boolean)
        .join(' – ');
      useEnrichmentQueueStore.getState().enqueue({
        messageId: data.enrichmentMessageId,
        type: 'contact_unlock',
        label: label.slice(0, 60),
        dealId: payload.dealId,
        contactCoreId: data.contactId,
        cnpj: payload.companyCnpj,
        startedAt: Date.now(),
      });
    }
    return data;
  },

  /**
   * Bulk check which contact_core IDs the current tenant has already
   * unlocked (paid for). Returns a map of id → { unlocked_at } for
   * paid contacts; IDs absent from the map are still locked.
   */
  checkUnlocks: async (
    contactCoreIds: string[],
    dealId?: string,
  ): Promise<CheckUnlocksResponse> => {
    // RP-CT-01: reveal visibility is per (tenant, deal). Passing dealId scopes
    // the check to THIS deal, so a reveal on another deal doesn't show here.
    const { data } = await api.post<CheckUnlocksResponse>(
      '/contacts/unlocks/check',
      { contact_core_ids: contactCoreIds, deal_id: dealId },
    );
    return data;
  },

  reportInconsistency: async (payload: ContactReportPayload): Promise<ContactReportResponse> => {
    const { data } = await api.post<ContactReportResponse>('/contacts/reports', payload);
    return data;
  },

  getEnrichmentStatus: async (messageId: string): Promise<EnrichmentStatusResponse> => {
    const { data } = await api.get<EnrichmentStatusResponse>(
      `/contacts/enrichment-status/${messageId}`,
    );
    return data;
  },

  /**
   * Kick off the async "últimas 5 postagens do LinkedIn" enrichment for a
   * contact. Returns immediately with the job handle; the actual posts are
   * polled via {@link getEnrichmentStatus}.
   *
   * Side effect (mirrors {@link unlockContact}): feeds the global enrichment
   * queue store so the Header "Fila de enriquecimentos" icon + the
   * useEnrichmentQueueDetector poller pick the job up and fire the
   * toast/desktop notification on completion. Dedupe is by messageId.
   */
  enqueueLinkedInPosts: async (
    contactCoreId: string,
    opts?: { label?: string; dealId?: string; force?: boolean },
  ): Promise<{ enrichmentMessageId: string; status: string }> => {
    // `force=true` (the "Atualizar" path) bypasses the cache-first read,
    // re-fetches from the provider, mints a new version, and re-charges.
    // First-open / unlock CTAs omit it so the backend serves cache-first
    // (free if already unlocked / served from the pool for the charge).
    const qs = opts?.force ? '?force=true' : '';
    const { data } = await api.post<{ enrichmentMessageId: string; status: string }>(
      `/contacts/${encodeURIComponent(contactCoreId)}/linkedin-posts/async${qs}`,
      {},
    );
    if (data.enrichmentMessageId) {
      useEnrichmentQueueStore.getState().enqueue({
        messageId: data.enrichmentMessageId,
        type: 'linkedin_posts',
        label: (opts?.label ?? 'Postagens do LinkedIn').slice(0, 60),
        dealId: opts?.dealId ?? '',
        contactCoreId,
        startedAt: Date.now(),
      });
    }
    return data;
  },

  /**
   * Kick off the async "perfil completo + consolidação de experiências"
   * enrichment for a contact. Returns immediately with the job handle; the
   * consolidated profile is polled via {@link getEnrichmentStatus}.
   *
   * Same global-queue side effect as {@link enqueueLinkedInPosts}.
   */
  enqueueLinkedInExperience: async (
    contactCoreId: string,
    opts?: { label?: string; dealId?: string; force?: boolean },
  ): Promise<{ enrichmentMessageId: string; status: string }> => {
    // See `enqueueLinkedInPosts` — `force=true` re-fetches + re-versions +
    // re-charges; omitted = cache-first.
    const qs = opts?.force ? '?force=true' : '';
    const { data } = await api.post<{ enrichmentMessageId: string; status: string }>(
      `/contacts/${encodeURIComponent(contactCoreId)}/linkedin-experience/async${qs}`,
      {},
    );
    if (data.enrichmentMessageId) {
      useEnrichmentQueueStore.getState().enqueue({
        messageId: data.enrichmentMessageId,
        type: 'linkedin_experience',
        label: (opts?.label ?? 'Perfil & experiência').slice(0, 60),
        dealId: opts?.dealId ?? '',
        contactCoreId,
        startedAt: Date.now(),
      });
    }
    return data;
  },

  // RP-01 — poll the post-delivery reveal state for a single contact.
  // Used by useRevealStatus during the 5s polling window after unlockContact.
  getRevealStatus: async (contactCoreId: string): Promise<RevealStatusResponse> => {
    const { data } = await api.get<RevealStatusResponse>(
      `/contacts/${encodeURIComponent(contactCoreId)}/reveal-status`,
    );
    return data;
  },

  // RP-03 — multi-provider profile (consolidated view + per-provider data points).
  //
  // A 404 here is an EXPECTED state: the contact_core pool is shared and
  // stale references (deleted/merged contacts) surface as 404. The drawer
  // renders an empty-state fallback when profile is null, so we swallow the
  // 404 and skip the global red toast — every other status still raises.
  getProfile: async (contactCoreId: string): Promise<ContactProfile | null> => {
    try {
      const { data } = await api.get<ContactProfile>(
        `/contacts/${encodeURIComponent(contactCoreId)}/profile`,
        { __skipErrorNotification: true } as never,
      );
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * Read-back of persisted LinkedIn enrichment for a contact
   * (`GET /contacts/{id}/linkedin-data`). Cache-first, NO re-fetch and NO
   * charge — surfaces what's already been paid for / saved so the drawer
   * can render saved posts + experience instantly on open. The drawer
   * prefers this over a live enrichment poll, falling back to the poll
   * only while a fresh (forced) job is in-flight.
   *
   * A 404 (stale contact_core id) is an EXPECTED state — swallow it and
   * return null so the drawer just shows the idle CTAs.
   */
  getLinkedInData: async (contactCoreId: string): Promise<LinkedInDataReadback | null> => {
    try {
      const { data } = await api.get<LinkedInDataReadback>(
        `/contacts/${encodeURIComponent(contactCoreId)}/linkedin-data`,
        { __skipErrorNotification: true } as never,
      );
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * Pin a specific contact of a deal to the +SDR AI queue (Contatos tab
   * "+SDR AI" button). Wraps the BFF proxy
   * `POST /api/v1/pipeline/{dealId}/contacts/{contactId}/sdr-ai` which
   * forwards to sales-api. On 422 the backend rejects the pin because the
   * contact has no dialable phone — the BFF surfaces the upstream message
   * (`detail`) verbatim under `error.message`; we rethrow a typed Error so
   * the UI can toast the exact reason.
   */
  pinContactToSdr: async (dealId: string, contactId: string): Promise<void> => {
    try {
      await api.post(
        `/pipeline/${encodeURIComponent(dealId)}/contacts/${encodeURIComponent(contactId)}/sdr-ai`,
        {},
        { __skipErrorNotification: true } as never,
      );
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { message?: string; detail?: string } } })
        ?.response;
      const message =
        res?.data?.message ||
        res?.data?.detail ||
        'Não foi possível adicionar o contato ao +SDR AI.';
      throw new Error(message);
    }
  },

  /**
   * Remove the per-contact +SDR AI pin. Idempotent on the sales-api side —
   * the BFF DELETE proxy returns 200 `{ok:true}` whether or not a pin
   * existed for the (deal, contact) pair.
   */
  unpinContactFromSdr: async (dealId: string, contactId: string): Promise<void> => {
    try {
      await api.delete(
        `/pipeline/${encodeURIComponent(dealId)}/contacts/${encodeURIComponent(contactId)}/sdr-ai`,
        { __skipErrorNotification: true } as never,
      );
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { message?: string; detail?: string } } })
        ?.response;
      const message =
        res?.data?.message ||
        res?.data?.detail ||
        'Não foi possível remover o contato do +SDR AI.';
      throw new Error(message);
    }
  },

  icpFeedback: async (
    contactCoreId: string,
    signalType: 'anti_icp' | 'add_to_icp',
  ): Promise<void> => {
    await api.post(`/contacts/${encodeURIComponent(contactCoreId)}/icp-feedback`, {
      signalType,
    });
  },

  /**
   * Trigger a deliberate DEEP phone search for ONE contact via Apollo's
   * waterfall (opt-in; normal reveals are unaffected). The number is
   * delivered asynchronously (minutes), so this returns immediately with a
   * job handle and feeds the global enrichment queue store — exactly like
   * {@link enqueueLinkedInPosts} — so the Header badge + completion poller
   * pick it up and the contact's phone surfaces when it lands.
   *
   * `status` is `"processing"` normally, or `"disabled"` when the backend's
   * pubsub dispatch is off (then `enrichmentMessageId` is null and nothing
   * is enqueued).
   */
  forcePhone: async (
    contactCoreId: string,
    opts?: { label?: string; dealId?: string; cnpj?: string },
  ): Promise<{ enrichmentMessageId: string | null; status: string }> => {
    const qs = opts?.dealId ? `?deal_id=${encodeURIComponent(opts.dealId)}` : '';
    const { data } = await api.post<{ enrichmentMessageId: string | null; status: string }>(
      `/contacts/${encodeURIComponent(contactCoreId)}/force-phone${qs}`,
      {},
    );
    // Feed the global enrichment queue so the Header badge + completion poller
    // pick it up. The dedicated `phone_search` type gives the detector a long
    // grace (the Apollo waterfall can take minutes) and a phone-specific
    // arrival signal — distinct from the seconds-scale `contact_unlock` reveal.
    if (data.enrichmentMessageId) {
      useEnrichmentQueueStore.getState().enqueue({
        messageId: data.enrichmentMessageId,
        type: 'phone_search',
        label: (opts?.label ?? 'Buscar telefone').slice(0, 60),
        dealId: opts?.dealId ?? '',
        contactCoreId,
        cnpj: opts?.cnpj,
        startedAt: Date.now(),
      });
    }
    return data;
  },
};

/**
 * Response from GET /contacts/:id/reveal-status (RP-01).
 *
 * ``status`` is the FE-facing label:
 *  - ``processing``     — enrichment in-flight (poll again in 5s)
 *  - ``complete``       — both email + phone delivered, 5 cred debited
 *  - ``partial_email``  — only email delivered, 2 cred debited
 *  - ``partial_phone``  — only phone delivered, 3 cred debited
 *  - ``failed_no_charge`` — provider returned nothing OR worker swept after 5 min
 *
 * Stop polling on any non-processing state.
 */
/**
 * RP-03 — Multi-provider read shape from GET /contacts/:id/profile.
 *
 * `consolidated` is the picked-best view (provider priority + recency).
 * `dataPoints` is the full per-provider history — same value may appear
 * once with `providers: ['lusha','apify']` when multiple sources agree.
 */
export type ProviderName = 'lusha' | 'apify' | 'apollo' | 'bq_decisores' | string;

export interface DataPoint {
  provider: ProviderName;
  providers: ProviderName[];
  fieldKind: 'email' | 'phone' | string;
  fieldSubtype: string | null;
  value: string;
  valueNormalized: string | null;
  confidence: number | null;
  capturedAt: string | null;
  verifiedAt: string | null;
}

export interface ConsolidatedView {
  email: string | null;
  phone: string | null;
  hasEmail: boolean;
  hasPhone: boolean;
  providers: ProviderName[];
}

export interface ProviderIdentifier {
  provider: ProviderName;
  identifierKind: string;
  value: string;
  capturedAt: string | null;
  expiresAt: string | null;
  metadata: Record<string, unknown> | null;
}

export interface ContactProfile {
  contactCoreId: string;
  fullName: string | null;
  currentTitle: string | null;
  currentCompany: string | null;
  linkedinUrl: string | null;
  consolidated: ConsolidatedView;
  dataPoints: DataPoint[];
  providerIdentifiers: ProviderIdentifier[];
}

export interface RevealStatusResponse {
  status:
    | 'processing'
    | 'complete'
    | 'partial_email'
    | 'partial_phone'
    | 'failed_no_charge';
  billing_status: string;
  has_email: boolean;
  has_phone: boolean;
  email: string | null;
  telefone: string | null;
  linkedin_url: string | null;
  cost_credits: number;
  unlocked_at: string | null;
  ttl_remaining_sec: number | null;
}
